import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Camera, Plus, Pencil, Trash2, Wifi, WifiOff,
  Play, Loader2, Video, Monitor
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const DISRUPTIVE_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/qx3y7mcn_Recurso%202lgo%20disr.png";

const BRANDS = [
  { value: 'dahua', label: 'Dahua' },
  { value: 'hikvision', label: 'Hikvision' },
  { value: 'generic', label: 'Generica' },
];
const ROLES = [
  { value: 'entry', label: 'Entrada' },
  { value: 'exit', label: 'Salida' },
  { value: 'general', label: 'General' },
];
const BRAND_COLORS = { dahua: 'text-orange-400 border-orange-400/50', hikvision: 'text-cyan-400 border-cyan-400/50', generic: 'text-slate-400 border-slate-500' };
const ROLE_COLORS = { entry: 'bg-emerald-600', exit: 'bg-red-600', general: 'bg-blue-600' };

export default function CamerasPage() {
  const { user } = useAuth();
  const [cameras, setCameras] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(null);
  const [form, setForm] = useState({
    name: '', brand: 'generic', type: 'ip', rtsp_url: '',
    channel: 1, role: 'general', enabled: true, connector_id: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [camRes, conRes] = await Promise.all([
        axios.get(`${API}/cameras`, { withCredentials: true }),
        axios.get(`${API}/connectors`, { withCredentials: true })
      ]);
      setCameras(camRes.data);
      setConnectors(conRes.data);
    } catch {} finally { setIsLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', brand: 'generic', type: 'ip', rtsp_url: '', channel: 1, role: 'general', enabled: true, connector_id: '' });
    setShowModal(true);
  };

  const openEdit = (cam) => {
    setEditing(cam);
    setForm({
      name: cam.name, brand: cam.brand, type: cam.type, rtsp_url: cam.rtsp_url,
      channel: cam.channel, role: cam.role, enabled: cam.enabled, connector_id: cam.connector_id || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('El nombre es obligatorio'); return; }
    setIsSaving(true);
    try {
      const payload = { ...form, connector_id: form.connector_id || null };
      if (editing) {
        await axios.put(`${API}/cameras/${editing.id}`, payload, { withCredentials: true });
        toast.success('Camara actualizada');
      } else {
        await axios.post(`${API}/cameras`, payload, { withCredentials: true });
        toast.success('Camara creada');
      }
      setShowModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta camara?')) return;
    try {
      await axios.delete(`${API}/cameras/${id}`, { withCredentials: true });
      toast.success('Camara eliminada');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleTest = async (cam) => {
    setIsTesting(cam.id);
    try {
      const { data } = await axios.post(`${API}/cameras/test`, {
        name: cam.name, brand: cam.brand, rtsp_url: cam.rtsp_url
      }, { withCredentials: true });
      toast.success(data.message);
    } catch (err) { toast.error('Error al testear'); }
    finally { setIsTesting(null); }
  };

  const getConnectorName = (cid) => connectors.find(c => c.id === cid)?.name || '-';

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><Button variant="ghost" size="sm" className="text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" />Volver</Button></Link>
            <div className="border-l border-slate-700 pl-4">
              <h1 className="text-lg font-bold text-white">Configuracion de Camaras</h1>
              <p className="text-sm text-slate-400">CRUD de camaras IP (Dahua, Hikvision, Generica)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-camera-btn"><Plus className="w-4 h-4 mr-2" />Nueva Camara</Button>
            <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 flex-1">
        {isLoading ? (
          <div className="text-center py-12 text-slate-400">Cargando...</div>
        ) : cameras.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <Camera className="w-16 h-16 mx-auto mb-4 opacity-40" />
            <p className="text-lg">No hay camaras configuradas</p>
            <p className="text-sm mt-1">Agrega tu primera camara para empezar</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cameras.map((cam) => (
              <Card key={cam.id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors" data-testid={`camera-card-${cam.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cam.enabled ? 'bg-blue-500/20' : 'bg-slate-700'}`}>
                        <Video className={`w-5 h-5 ${cam.enabled ? 'text-blue-400' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <p className="text-white font-semibold">{cam.name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Badge variant="outline" className={`text-xs ${BRAND_COLORS[cam.brand] || BRAND_COLORS.generic}`}>{cam.brand}</Badge>
                          <Badge className={`text-xs ${ROLE_COLORS[cam.role] || ROLE_COLORS.general}`}>
                            {ROLES.find(r => r.value === cam.role)?.label || cam.role}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {cam.enabled ? <Wifi className="w-4 h-4 text-emerald-400" /> : <WifiOff className="w-4 h-4 text-red-400" />}
                    </div>
                  </div>
                  {cam.rtsp_url && (
                    <p className="text-slate-500 text-xs font-mono truncate mb-2" title={cam.rtsp_url}>{cam.rtsp_url}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                    <span>Canal: {cam.channel}</span>
                    <span>Conector: {getConnectorName(cam.connector_id)}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => handleTest(cam)} disabled={isTesting === cam.id}
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700" data-testid={`test-camera-${cam.id}`}>
                      {isTesting === cam.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
                      Test
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(cam)} className="text-blue-400 hover:text-blue-300">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(cam.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <footer className="py-3 sm:py-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-3 flex flex-col sm:flex-row items-center justify-between gap-1">
          <img src={DISRUPTIVE_LOGO} alt="Disruptive" className="h-6 w-auto invert" />
          <span className="text-slate-500 text-xs">Desarrollado por <a href="https://www.disruptive-sw.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Disruptive Software</a>. Todos los derechos reservados.</span><span className="text-slate-500 text-xs ml-auto"><a href="mailto:info@disruptive-sw.com" className="hover:text-white">info@disruptive-sw.com</a></span>
        </div>
      </footer>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5 text-blue-400" />{editing ? 'Editar Camara' : 'Nueva Camara'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-slate-300">Nombre</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" placeholder="Camara Entrada Principal" data-testid="camera-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Marca</Label>
                <Select value={form.brand} onValueChange={v => setForm({...form, brand: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="camera-brand-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {BRANDS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Rol</Label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="camera-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-slate-300">URL RTSP</Label><Input value={form.rtsp_url} onChange={e => setForm({...form, rtsp_url: e.target.value})} className="bg-slate-700 border-slate-600 text-white font-mono text-sm mt-1" placeholder="rtsp://admin:pass@192.168.1.100:554/cam/realmonitor?channel=1" data-testid="camera-rtsp-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Canal</Label><Input type="number" min={1} value={form.channel} onChange={e => setForm({...form, channel: parseInt(e.target.value) || 1})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="camera-channel-input" /></div>
              <div>
                <Label className="text-slate-300">Conector</Label>
                <Select value={form.connector_id || '_none'} onValueChange={v => setForm({...form, connector_id: v === '_none' ? '' : v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1"><SelectValue placeholder="Sin conector" /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="_none">Sin conector</SelectItem>
                    {connectors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.enabled} onCheckedChange={v => setForm({...form, enabled: v})} data-testid="camera-enabled-switch" />
              <Label className="text-slate-300">{form.enabled ? 'Habilitada' : 'Deshabilitada'}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-camera-btn">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
